import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import * as api from '../utils/api';
import { setClient as setClientAction } from '../actions/client';
import { resumeReparation } from '../utils/reparation';
import Container from '../components/shared/atoms/Container';
import Row from '../components/shared/atoms/Row';
import Box from '../components/shared/atoms/Box';
import Text from '../components/shared/atoms/Text';
import Button from '../components/shared/atoms/Button';
import Alert from '../components/shared/molecules/Alert';
import Loader from '../components/blocks/Loader';

const GoogleAuthCallback = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const store = useSelector((s) => s);
  const [status, setStatus] = useState('loading'); // 'loading' | 'error'
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (status !== 'loading') return;

    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const error = urlParams.get('error');
    const message = urlParams.get('message');
    const redir = urlParams.get('redir');

    const hasRepairSlots =
      store &&
      store.course &&
      Array.isArray(store.course.slots) &&
      store.course.slots.length > 0;

    const hasRepairContext =
      hasRepairSlots ||
      !!(store && store.diag && store.diag.devis && store.diag.devis.id);

    const redirectAfterLogin = () => {
      if (redir) {
        if (redir === 'reparation') {
          // Si l'utilisateur a déjà choisi des slots, on le redirige directement vers la confirmation
          // au lieu de recalculer l'étape via resumeReparation (qui pourrait le renvoyer au début)
          if (hasRepairSlots) {
            history.push('/reparation/confirmation');
          } else {
            resumeReparation(store, history);
          }
          return;
        }
        if (redir === 'giftcard') {
          // Aligné sur la logique de ConnexionModule : revenir sur la page d'achat
          history.push('/carte-cadeau/acheter');
          return;
        }
        if (redir.indexOf('/courses') === 0) {
          history.push(redir);
          return;
        }
        if (redir === 'account') {
          history.push('/account');
          return;
        }
        if (redir.indexOf('/') === 0) {
          history.push(redir);
          return;
        }
      } else if (hasRepairSlots) {
        history.push('/reparation/confirmation');
        return;
      } else if (hasRepairContext) {
        resumeReparation(store, history);
        return;
      }

      history.push('/account');
    };

    if (token) {
      (async () => {
        try {
          const client = await api.get('/clients/sessions', {}, { token });
          if (client && client.id && client.token) {
            dispatch(setClientAction(client));
            // La collecte / validation du téléphone est désormais gérée
            // uniquement via la modale globale PhoneRequiredModal.
            // Ici, on se contente de rediriger vers la bonne page.
            redirectAfterLogin();
          } else {
            setStatus('error');
            setErrorMessage(
              t(
                'googleauth.error.genericLogin',
                'Impossible de se connecter avec Google, veuillez réessayer ou utiliser votre email/mot de passe.',
              ),
            );
          }
        } catch (e) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.error('Google auth callback error', e);
          }
          setStatus('error');
          setErrorMessage(
            t(
              'googleauth.error.genericLogin',
              'Impossible de se connecter avec Google, veuillez réessayer ou utiliser votre email/mot de passe.',
            ),
          );
        }
      })();
    } else if (error) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Google auth error', { error, message });
      }
      setStatus('error');
      setErrorMessage(
        message ||
          t(
            'googleauth.error.genericLogin',
            'Impossible de se connecter avec Google, veuillez réessayer ou utiliser votre email/mot de passe.',
          ),
      );
    } else {
      setStatus('error');
      setErrorMessage(
        t(
          'googleauth.error.genericLogin',
          'Impossible de se connecter avec Google, veuillez réessayer ou utiliser votre email/mot de passe.',
        ),
      );
    }
  }, [dispatch, history, store, t, status]);

  const handleRetry = () => {
    if (typeof window === 'undefined') {
      history.push('/login');
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const redir = urlParams.get('redir');
    const qs = redir ? `?redir=${encodeURIComponent(redir)}` : '';
    history.push(`/login${qs}`);
  };

  const handleFallback = () => {
    history.push('/login');
  };

  const noindexHelmet = (
    <Helmet meta={[{ name: 'robots', content: 'noindex, nofollow' }]} />
  );

  if (status === 'loading') {
    return (
      <Container mt={5} mb={10}>
        {noindexHelmet}
        <Row
          md
          gap={24}
          style={{ alignItems: 'center', justifyContent: 'center' }}>
          <Box>
            <Loader />
            <Text mt={4}>
              {t('googleauth.loading', 'Connexion en cours avec Google...')}
            </Text>
          </Box>
        </Row>
      </Container>
    );
  }

  return (
    <Container mt={5} mb={10}>
      {noindexHelmet}
      <Row
        md
        gap={24}
        style={{ alignItems: 'center', justifyContent: 'center' }}>
        <Box>
          {errorMessage && (
            <Alert status="danger" mb={4}>
              {errorMessage}
            </Alert>
          )}
          <Button
            variant="filled"
            onClick={handleRetry}
            data-testid="google-auth-retry-button">
            {t('googleauth.retry', 'Réessayer avec Google')}
          </Button>
          <Box mt={3}>
            <Button
              variant="ghost"
              onClick={handleFallback}
              data-testid="google-auth-fallback-email-button">
              {t(
                'googleauth.fallbackEmail',
                'Se connecter avec email / mot de passe',
              )}
            </Button>
          </Box>
        </Box>
      </Row>
    </Container>
  );
};

export default GoogleAuthCallback;
